/**
 * Knockout-bracket placeholder injector.
 *
 * Pure function — given an array of real matches (as returned by
 * `GET /api/tournaments/:id/matches?all=true`) and a tournament identity,
 * inject synthetic placeholder match objects for any knockout stage the API
 * has not yet populated. As soon as the API publishes at least one real
 * fixture for a stage, the injector stops emitting placeholders for that
 * stage (the API has taken over and we trust its data).
 *
 * The injector consults `getCompetitionRule(leagueCode, season)` to discover
 * the bracket template. If no rule is configured for the tournament's
 * identity, or the rule has no `bracket`, the input array is returned
 * unchanged.
 *
 * Synthetic matches use negative numeric ids (`placeholderId`) to guarantee
 * no collision with real `matches.id` PKs (which are non-negative serials).
 * They also carry `team1` / `team2` as empty strings so the existing
 * `MatchPreviewCard` / `MatchDetailCard` placeholder-rendering branch picks
 * them up and renders the localised placeholder labels.
 */

import { getCompetitionRule } from './competitionRules';
import type { KnockoutSlot, KnockoutStage } from './competitionRules/brackets/types';

/** Minimal match shape required by the injector. Matches the runtime payload. */
export interface InjectableMatch {
  id: number;
  tournament_id?: number;
  team1: string;
  team2: string;
  team1_placeholder?: string | null;
  team2_placeholder?: string | null;
  status: string;
  match_date: string;
  stage?: string;
  /**
   * Marker for frontend-only placeholder matches injected by this module.
   * Downstream consumers (e.g. `buildStageMatchdays` /
   * `detectInitialMatchday`) use this to distinguish synthetic stages from
   * real ones and avoid auto-selecting a stage that has no real data yet.
   */
  isSynthetic?: boolean;
}

/**
 * Tournament identity shape — accepts both possible field name conventions:
 * - Flat `leagueCode` / `season` (this is what `formatTournament` in the
 *   backend emits today — used by `TournamentDetailLayout.tournament`).
 * - Nested `league.externalId` / `league.season` (forward-compatible — if
 *   the backend ever moves these fields under the nested league object).
 *
 * The `league` property is typed as a permissive open-record shape because
 * the existing frontend `Tournament` types already declare `league` as
 * `{ name; logo_url }` — we don't want to force a structural narrowing on
 * the caller. `resolveIdentity` reads `league.externalId` / `league.season`
 * with optional chaining, so missing fields are treated as absent.
 */
export interface InjectableTournament {
  leagueCode?: string;
  season?: string | number;
  league?: Record<string, unknown>;
}

/**
 * Knockout stages in the order they're emitted. Filtering uses
 * `KNOCKOUT_STAGES`'s membership to know which stages the injector owns —
 * any other `match.stage` value (group stage, regular season, etc.) is
 * irrelevant to the gating logic.
 */
const KNOCKOUT_STAGES: KnockoutStage[] = [
  'Round of 32',
  'Round of 16',
  'Quarter-finals',
  'Semi-finals',
  '3rd Place Final',
  'Final',
];

/** What a placeholder stands for. `leg` 0 is a tie as a whole, 1 and up one of its matches. */
export interface PlaceholderIdParts {
  /** `leagues.external_id` - numeric at the source. */
  leagueCode: string;
  /** Season string; its leading year is used ("2026", "2026/27"). */
  season: string;
  /** 0-based round of the competition's template, below 10. */
  round: number;
  /** Slot within the round, below 100. */
  slot: number;
  /** Below 10. */
  leg: number;
}

/**
 * Id of a frontend-only placeholder: negative, so it never meets a stored
 * `matches.id`, and one decimal field per part - league code, 4 digits of
 * season, 1 of round, 2 of slot, 1 of leg - so two placeholders that differ in
 * any part never share an id. Throws on a part that does not fit its field.
 */
export function placeholderId({ leagueCode, season, round, slot, leg }: PlaceholderIdParts): number {
  const fields: [value: number, size: number][] = [
    [Number(leagueCode), 10_000_000],
    [Number.parseInt(season, 10), 10_000],
    [round, 10],
    [slot, 100],
    [leg, 10],
  ];
  return -fields.reduce((id, [value, size]) => {
    if (!Number.isInteger(value) || value < 0 || value >= size) {
      throw new RangeError(`Placeholder id part out of range: ${value}`);
    }
    return id * size + value;
  }, 1);
}

/** Build a synthetic match from a knockout slot. */
function slotToSyntheticMatch(slot: KnockoutSlot, leagueCode: string, season: string): InjectableMatch {
  const id = placeholderId({
    leagueCode,
    season,
    round: KNOCKOUT_STAGES.indexOf(slot.stage),
    slot: slot.slotIndex,
    leg: 1,
  });

  // Note: nullable / optional fields (`score_team1`, `score_team2`, `matchday`,
  // `user_prediction`, logos) are intentionally omitted rather than set to
  // `null`, because both consuming `Match` type variants
  // (`TournamentDetailLayout/context.tsx` and `MatchDetailLayout/context.tsx`)
  // declare them as `?: number | undefined` (no `| null`). Omitting keeps the
  // type-cast `as T[]` clean while preserving the same runtime semantics —
  // downstream code already gates on `??` / truthiness for these fields.
  return {
    id,
    tournament_id: 0,
    team1: '',
    team2: '',
    team1_placeholder: slot.team1Placeholder,
    team2_placeholder: slot.team2Placeholder,
    status: 'SCHEDULED',
    match_date: slot.matchDateUtc,
    stage: slot.stage,
    isSynthetic: true,
  };
}

/**
 * Returns the resolved `(leagueCode, season)` pair for the tournament, picking
 * whichever side the backend actually populated. Returns `null` when either
 * piece is missing - a dated schedule cannot be tied to its edition without
 * both. Season is normalised to a string for comparison.
 */
function resolveIdentity(
  tournament: InjectableTournament | null | undefined,
): { leagueCode: string; season: string } | null {
  if (!tournament) return null;
  const nestedExternalId = tournament.league?.externalId;
  const nestedSeason = tournament.league?.season;
  const leagueCode =
    tournament.leagueCode ??
    (typeof nestedExternalId === 'string' || typeof nestedExternalId === 'number'
      ? nestedExternalId
      : undefined);
  const seasonRaw =
    tournament.season ??
    (typeof nestedSeason === 'string' || typeof nestedSeason === 'number'
      ? nestedSeason
      : undefined);
  if (leagueCode == null || seasonRaw == null) return null;
  return {
    leagueCode: String(leagueCode),
    season: String(seasonRaw),
  };
}

/**
 * Inject knockout placeholder matches into the matches list for any stage the
 * backend hasn't published yet. Pure — input array is not mutated.
 *
 * The bracket template is sourced from the matching `CompetitionRule` via
 * `getCompetitionRule(leagueCode, season)`. Tournaments without a configured
 * rule (or with a rule that omits `bracket`) get the input array back
 * unchanged.
 */
export function injectKnockoutBracket<T extends InjectableMatch>(
  matches: T[],
  tournament: InjectableTournament | null | undefined,
): T[] {
  const identity = resolveIdentity(tournament);
  if (!identity) return matches;
  const rule = getCompetitionRule(identity.leagueCode, identity.season);
  if (!rule?.bracket) return matches;
  const bracket = rule.bracket;

  // Determine which knockout stages already have at least one real match in
  // the API response — those stages are NOT injected.
  const stagesWithRealMatches = new Set<string>();
  for (const m of matches) {
    if (m.stage && KNOCKOUT_STAGES.includes(m.stage as KnockoutStage)) {
      stagesWithRealMatches.add(m.stage);
    }
  }

  const synthetic: InjectableMatch[] = [];
  for (const stage of KNOCKOUT_STAGES) {
    if (stagesWithRealMatches.has(stage)) continue;
    for (const slot of bracket) {
      if (slot.stage === stage) {
        synthetic.push(slotToSyntheticMatch(slot, identity.leagueCode, identity.season));
      }
    }
  }

  if (synthetic.length === 0) return matches;

  // Append — downstream `buildStageMatchdays` groups & orders by stage/date,
  // so order at this layer doesn't matter.
  return [...matches, ...(synthetic as T[])];
}
